import { FieldArray, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Wealthlane_TaxService_Dtos_TaxItemDto_JsonDto } from '../../../../services/wealthlane-tax-services';
import useWealthlaneTaxApi from '../../../../hooks/useWealthlaneTaxApi';
import {
  useGetGrantById,
  useGetParticipantViewDetails,
  useGetPlanById,
  useGetTaxCityOptions,
  useGetTaxCountryOptions,
  useGetTaxJuridictionLevel,
  useGetTaxPeriodVendor,
  useGetTaxStateOptions,
  useGetTransactionById,
  useWealthlaneAwardApi,
  useWealthlaneCustomerApi,
} from '../../../../hooks';
import InitialValuesForm from '../../../../constants/initialValues';
import { OptionType } from '../../../../types/common';
import ModalBodyComponent from '../../../../components/modal/modalBodyComponent';
import {
  TaxBracketAccordingContainer,
  TaxBracketAccordingValue,
  TaxBracketHeaders,
  TaxBracketValue,
  ViewData,
} from '../tax-setup/taxSetupStyled';
import Tag from '../../../../components/lozenge/tags';
import Heading from '../../../../components/typography';
import { COLORS } from '../../../../constants/style/color';
import { UserDefinedCriteriaComponent } from '../tax-setup/tax-block';
import { motion } from 'framer-motion';
import InputText from '../../../../components/input-text/inputText';
import moment from 'moment';
import { DATE_FORMAT } from '../../../../constants/common';
import { DescriptionType } from '../tax-setup/taxConstantTypes';

interface TaxLibraryViewType {
  viewData?: any;
  id?: string;
}

const TaxLibraryView = ({ viewData, id }: TaxLibraryViewType) => {
  const { handleRequest } = useWealthlaneTaxApi();
  const { handleRequest: participantRequest } = useWealthlaneCustomerApi();
  const { data: juridictionLevelOptions } = useGetTaxJuridictionLevel(handleRequest);
  const [descriptionData, setDescriptionData] = useState<DescriptionType[]>([]);

  const { handleRequest: awardRequest } = useWealthlaneAwardApi();
  const [metaValidation, setMetaValidation] = useState<
    Wealthlane_TaxService_Dtos_TaxItemDto_JsonDto | undefined
  >();
  const [hide, setHide] = useState<boolean>(false);
  const optionState = {
    countryId: '',
    stateId: '',
  };

  const initialData = {
    ...viewData,
    taxBrackets: viewData?.taxBrackets?.map((item: any) => ({
      lowerLimit: item?.lowerLimit,
      taxRate: item?.taxRate === 0 ? '0' : item?.taxRate,
      upperLimit: item?.upperLimit,
    })),
  };
  const [stateArrayOptions, setStateArrayOptions] = useState<any>(null);
  const [cityArrayOptions, setCityArrayOptions] = useState<any>(null);
  const [optionSelectedState, setOptionSelectedState] =
    useState<Record<string, string>>(optionState);

  const { data: countryOptions } = useGetTaxCountryOptions(handleRequest);
  const { data: stateOptions } = useGetTaxStateOptions(
    handleRequest,
    optionSelectedState?.countryId !== '' ? optionSelectedState?.countryId : viewData?.countryId
  );
  const { data: cityOptions } = useGetTaxCityOptions(
    handleRequest,
    optionSelectedState?.stateId !== '' ? optionSelectedState?.stateId : viewData?.stateId
  );

  return (
    <Formik enableReinitialize initialValues={initialData} onSubmit={(values) => { }}>
      {({ touched, handleChange, errors, setFieldValue, setFieldTouched, values }) => {
        const metaData: Wealthlane_TaxService_Dtos_TaxItemDto_JsonDto | undefined =
          juridictionLevelOptions?.filter(
            (item: OptionType) => item?.value === values?.jurisdictionLevelId
          )[0]?.metadata;

        useEffect(() => {
          setMetaValidation(metaData);
        }, [metaData]);

        return (
          <Form id="taxLibraryViewForm">
            {/* <ModalBodyComponent> */}
            <ViewData id="taxLibraryViewTaxItemDetails">
              <Tag variant='info' title={'Tax Item Details'} color={COLORS.Gray} id="taxLibraryViewTaxItemDetailsTag" />
              <div className='grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 mt-5 gap-5' id="taxLibraryViewTaxItemDetailsGrid">
                {viewData?.name != null && (
                  <div id="taxLibraryViewNameContainer">
                    <Heading variant='body-xs' title={'Name'} id="taxLibraryViewNameLabel" />
                    <Heading
                      variant='h6'
                      title={`${viewData?.name != null ? viewData?.name : '-'}`}
                      id="taxLibraryViewNameValue"
                    />
                  </div>
                )}
                {viewData?.taxPeriodName != null && (
                  <div id="taxLibraryViewTaxPeriodContainer">
                    <Heading variant='body-xs' title={'Tax Period'} color={COLORS.Gray} id="taxLibraryViewTaxPeriodLabel" />
                    <Heading variant='h6' title={`${viewData?.taxPeriodName}`} id="taxLibraryViewTaxPeriodValue" />
                  </div>
                )}
                {viewData?.jurisdictionName != null && (
                  <div id="taxLibraryViewJurisdictionContainer">
                    <Heading variant='body-xs' title={'Jurisdiction'} color={COLORS.Gray} id="taxLibraryViewJurisdictionLabel" />
                    <Heading
                      variant='h6'
                      title={`${viewData?.jurisdictionName !== '' ? viewData?.jurisdictionName : '-'
                        }`}
                      id="taxLibraryViewJurisdictionValue"
                    />
                  </div>
                )}
                {viewData?.statusDisplayName != null && (
                  <div id="taxLibraryViewStatusContainer">
                    <Heading variant='body-xs' title={'Status'} color={COLORS.Gray} id="taxLibraryViewStatusLabel" />
                    <Heading variant='h6' title={`${viewData?.statusDisplayName}`} id="taxLibraryViewStatusValue" />
                  </div>
                )}
              </div>
            </ViewData>
            {viewData?.countryName != null && (
              <ViewData id="taxLibraryViewJurisdictionSection">
                <Tag variant='info' title={'Jurisdiction'} id="taxLibraryViewJurisdictionSectionTag" />
                <div className='grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 mt-5' id="taxLibraryViewJurisdictionSectionGrid">
                  <div id="taxLibraryViewCountryContainer">
                    <Heading variant='body-xs' title={'Country'} color={COLORS.Gray} id="taxLibraryViewCountryLabel" />
                    <Heading variant='h6' title={viewData?.countryName} id="taxLibraryViewCountryValue" />
                  </div>
                  {viewData?.stateName && (
                    <div id="taxLibraryViewStateContainer">
                      <Heading variant='body-xs' title={'State'} color={COLORS.Gray} id="taxLibraryViewStateLabel" />
                      <Heading variant='h6' title={viewData?.stateName} id="taxLibraryViewStateValue" />
                    </div>
                  )}
                  {viewData?.cityName && (
                    <div id="taxLibraryViewCityContainer">
                      <Heading variant='body-xs' title={'City'} color={COLORS.Gray} id="taxLibraryViewCityLabel" />
                      <Heading variant='h6' title={viewData?.cityName} id="taxLibraryViewCityValue" />
                    </div>
                  )}
                </div>
              </ViewData>
            )}
            {(metaData?.allowUserDefinedCriteria ?? false) && (
              <div id="taxLibraryViewUserDefinedCriteriaContainer">
                <UserDefinedCriteriaComponent
                  handleRequest={handleRequest}
                  metaData={metaData}
                  handleChange={handleChange}
                  errors={errors}
                  touched={touched}
                  values={values}
                  setFieldValue={setFieldValue}
                  modalStatus={'View'}
                  jurisdictionOptions={juridictionLevelOptions}
                  setDescriptionData={setDescriptionData}
                  descriptionData={descriptionData}
                  id="taxLibraryViewUserDefinedCriteria"
                />
              </div>
            )}
            <ViewData id="taxLibraryViewTaxBracketSection">
              <Tag variant='info' title={'Tax Bracket'} id="taxLibraryViewTaxBracketSectionTag" />
              <TaxBracketAccordingContainer className='flex justify-between mt-5' id="taxLibraryViewTaxBracketHeader">
                <Heading variant='body' title={'Tax Bracket'} id="taxLibraryViewTaxBracketTitle" />
                <div
                  className='flex'
                  onClick={() => {
                    setHide(!hide);
                  }}
                  id="taxLibraryViewTaxBracketToggle">
                  {!hide ? (
                    <>
                      {/* <Heading variant='body-s' title={'Hide'} /> */}
                      <span className='material-symbols-outlined image-icon' id="taxLibraryViewTaxBracketExpandLess">expand_less</span>
                    </>
                  ) : (
                    <>
                      {/* <Heading variant='body-s' title={'Show'} /> */}
                      <span className='material-symbols-outlined image-icon' id="taxLibraryViewTaxBracketExpandMore">expand_more</span>
                    </>
                  )}
                </div>
              </TaxBracketAccordingContainer>
              <FieldArray
                name='taxBracket'
                render={(arrayHelpers) => {
                  return (
                    <div id="taxLibraryViewTaxBracketFieldArray">
                      {!hide && (
                        <motion.div
                          initial={{ y: -2, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{
                            delay: 0.5,
                            duration: 0.3,
                          }}
                          id="taxLibraryViewTaxBracketContent">
                          <TaxBracketAccordingValue id="taxLibraryViewTaxBracketAccordingValue">
                            <motion.div initial={{ y: 10 }} animate={{ y: 0 }} id="taxLibraryViewTaxBracketMotion">
                              <TaxBracketHeaders className='grid grid-cols-12 gap-x-5' id="taxLibraryViewTaxBracketHeaders">
                                <div className='col-span-4' id="taxLibraryViewTaxRateHeader">
                                  <Heading
                                    variant='body-xs'
                                    title={'Tax Rate (%)'}
                                    color={COLORS.Gray}
                                    id="taxLibraryViewTaxRateHeaderTitle"
                                  />
                                </div>
                                <div className='col-span-4' id="taxLibraryViewLowerBracketHeader">
                                  <Heading
                                    variant='body-xs'
                                    title={'Lower Bracket Limit ($)'}
                                    color={COLORS.Gray}
                                    id="taxLibraryViewLowerBracketHeaderTitle"
                                  />
                                </div>
                                <div className='col-span-4' id="taxLibraryViewUpperBracketHeader">
                                  <Heading
                                    variant='body-xs'
                                    title={'Upper Bracket Limit ($)'}
                                    color={COLORS.Gray}
                                    id="taxLibraryViewUpperBracketHeaderTitle"
                                  />
                                </div>
                              </TaxBracketHeaders>
                              <div id="taxLibraryViewTaxBracketRows">
                                {values?.taxBrackets != null &&
                                  values?.taxBrackets.length > 0 &&
                                  values?.taxBrackets?.map((taxBracket: any, index: number) => (
                                    <TaxBracketValue
                                      key={'1'}
                                      className='grid lg:grid-cols-12 md:grid-cols-4 grid-cols-1 items-start gap-x-5'
                                      id={`taxLibraryViewTaxBracketRow.${index}`}>
                                      <div className='col-span-4' id={`taxLibraryViewTaxRateContainer.${index}`}>
                                        <div className='grid lg:grid-cols-2 grid-cols-1' id={`taxLibraryViewTaxRateGrid.${index}`}>
                                          <InputText
                                            type='text'
                                            name={`taxBracket[${index}].taxRate`}
                                            id={`taxBracket.${index}.taxRate`}
                                            onChange={handleChange}
                                            errors={errors}
                                            touched={touched}
                                            value={taxBracket?.taxRate}
                                            disabled
                                            required
                                          />
                                        </div>
                                      </div>
                                      <div className='col-span-4' id={`taxLibraryViewLowerBracketContainer.${index}`}>
                                        <div className=' grid lg:grid-cols-3 grid-cols-1' id={`taxLibraryViewLowerBracketGrid.${index}`}>
                                          <InputText
                                            className='col-span-2'
                                            type='text'
                                            name={`taxBracket[${index}].lowerBracketLimit`}
                                            id={`taxBracket.${index}.lowerBracketLimit`}
                                            onChange={handleChange}
                                            errors={errors}
                                            touched={touched}
                                            value={taxBracket?.lowerLimit}
                                            disabled
                                            required
                                          />
                                        </div>
                                      </div>
                                      <div className='col-span-4' id={`taxLibraryViewUpperBracketContainer.${index}`}>
                                        <div className=' grid lg:grid-cols-3 grid-cols-1' id={`taxLibraryViewUpperBracketGrid.${index}`}>
                                          <InputText
                                            className=' col-span-2'
                                            type='text'
                                            name={`taxBracket[${index}].upperBracketLimit`}
                                            id={`taxBracket.${index}.upperBracketLimit`}
                                            onChange={handleChange}
                                            errors={errors}
                                            touched={touched}
                                            value={taxBracket?.upperLimit}
                                            disabled
                                            required
                                          />
                                        </div>
                                      </div>
                                    </TaxBracketValue>
                                  ))}
                              </div>
                            </motion.div>
                          </TaxBracketAccordingValue>
                        </motion.div>
                      )}
                    </div>
                  );
                }}
              />
            </ViewData>
            <ViewData className='' id="taxLibraryViewIncomeYtdTypeSection">
              {viewData?.incomeYTDTypeDisplayName != null && (
                <div id="taxLibraryViewIncomeYtdTypeContainer">
                  <Heading variant='body-xs' title={'Income YTD Type'} color={COLORS.Gray} id="taxLibraryViewIncomeYtdTypeLabel" />
                  <Heading variant='h6' title={`${viewData?.incomeYTDTypeDisplayName}`} id="taxLibraryViewIncomeYtdTypeValue" />
                </div>
              )}
            </ViewData>
            <ViewData className='border-0' id="taxLibraryViewApplicableSection">
              <Tag variant='info' title={'Applicable'} id="taxLibraryViewApplicableSectionTag" />
              <div className='grid grid-cols-4  gap-5 mt-5' id="taxLibraryViewApplicableSectionGrid">
                {viewData?.equityCompensationLevelName != null && (
                  <div id="taxLibraryViewApplicableToContainer">
                    <Heading variant='body-xs' title={'Applicable To'} color={COLORS.Gray} id="taxLibraryViewApplicableToLabel" />
                    <Heading variant='h6' title={`${viewData?.equityCompensationLevelName}`} id="taxLibraryViewApplicableToValue" />
                  </div>
                )}
              </div>
            </ViewData>
            {/* </ModalBodyComponent> */}
          </Form>
        );
      }}
    </Formik>
  );
};

export default TaxLibraryView;
